import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getCache, setCache } from '../../utils/cache';
import '../../styles/components.css';

const JourneySoFar: React.FC = () => {
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const isViewer = userProfile?.role?.toLowerCase().includes('viewer');

  const [images, setImages] = useState<string[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchImages();
  }, [companyId]);

  useEffect(() => {
    generateSignedUrls(images);
  }, [images]);

  const generateSignedUrls = async (imageUrls: string[]) => {
    const newSignedUrls: Record<string, string> = {};
    const pathsToSign: { url: string, path: string }[] = [];

    for (const rawUrl of imageUrls) {
      if (signedUrls[rawUrl]) continue; // Skip if already generated

      let path = rawUrl;
      if (path.includes('/object/public/company-documents/')) {
        path = path.split('/object/public/company-documents/')[1].split('?')[0];
      } else if (path.includes('/company-documents/')) {
        path = path.split('/company-documents/')[1].split('?')[0];
      } else if (path.startsWith('company-documents/')) {
        path = path.substring('company-documents/'.length);
      } else if (path.startsWith('http')) {
        // External URL, no need to sign
        newSignedUrls[rawUrl] = rawUrl;
        continue;
      }

      if (path) {
        pathsToSign.push({ url: rawUrl, path: decodeURIComponent(path) });
      }
    }

    if (pathsToSign.length > 0) {
      try {
        const { data, error } = await supabase.storage.from('company-documents').createSignedUrls(pathsToSign.map(p => p.path), 3600);
        if (data && !error) {
          data.forEach((item, i) => {
            if (item.signedUrl) {
              newSignedUrls[pathsToSign[i].url] = item.signedUrl;
            } else {
              newSignedUrls[pathsToSign[i].url] = pathsToSign[i].url; // Fallback
            }
          });
        }
      } catch (err) {
        console.error('Failed to bulk sign journey urls:', err);
      }
    }

    if (Object.keys(newSignedUrls).length > 0) {
      setSignedUrls(prev => ({ ...prev, ...newSignedUrls }));
    }
  };

  const fetchImages = async () => {
    if (!companyId) return;
    
    const cachedImages = getCache(`journey_${companyId}`);
    if (cachedImages) {
      setImages(cachedImages);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('companies')
        .select('journey_images')
        .eq('id', companyId)
        .single();
        
      if (error) {
        // If the column doesn't exist yet, it might throw an error.
        console.error('Error fetching journey images:', error);
        setError('Could not fetch images. Have you run the SQL script to add the journey_images column?');
      } else {
        const fetchedImages = data?.journey_images || [];
        setImages(fetchedImages);
        setCache(`journey_${companyId}`, fetchedImages);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    if (!companyId) return;
    
    setUploading(true);
    setError('');
    
    const newFiles = Array.from(e.target.files);
    const uploadedUrls: string[] = [];
    
    try {
      for (const file of newFiles) {
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `${companyId}/journey-${Date.now()}-${safeName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(fileName, file);
          
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('company-documents')
          .getPublicUrl(fileName);
          
        uploadedUrls.push(publicUrl);
      }
      
      const updatedImages = [...images, ...uploadedUrls];
      
      const { error: updateError } = await supabase
        .from('companies')
        .update({ journey_images: updatedImages })
        .eq('id', companyId);
        
      if (updateError) throw updateError;
      
      setImages(updatedImages);
      setCache(`journey_${companyId}`, updatedImages);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError(err.message || 'Failed to upload images');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = async (urlToRemove: string) => {
    if (isViewer) return;
    if (!window.confirm("Are you sure you want to remove this image?")) return;
    
    const updatedImages = images.filter(url => url !== urlToRemove);
    try {
      const { error: updateError } = await supabase
        .from('companies')
        .update({ journey_images: updatedImages })
        .eq('id', companyId);
        
      if (updateError) throw updateError;
      setImages(updatedImages);
      setCache(`journey_${companyId}`, updatedImages);
    } catch (err: any) {
      console.error('Failed to remove image:', err);
      setError('Failed to remove image');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', maxWidth: '1600px', margin: '0 auto' }}>
      {!isViewer && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <div>
            <input 
              type="file" 
              multiple 
              accept="image/*" 
              style={{ display: 'none' }} 
              ref={fileInputRef}
              onChange={handleFileUpload}
            />
            <button 
              className="btn btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Add Images'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ backgroundColor: '#fee2e2', color: '#ef4444', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      {images.length === 0 && !loading && !error ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '4rem 2rem', 
          backgroundColor: '#ffffff', 
          borderRadius: '12px',
          border: '1px dashed var(--border)',
          color: 'var(--text-secondary)'
        }}>
          <h3>No images added yet.</h3>
          <p>Upload photos to showcase your journey!</p>
        </div>
      ) : (
        <div style={{ 
          display: 'flex',
          flexDirection: 'column', 
          gap: '2.5rem' 
        }}>
          {images.map((url, index) => (
            <div 
              key={index} 
              style={{ 
                position: 'relative', 
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <img 
                src={signedUrls[url] || url} 
                alt={`Journey ${index + 1}`} 
                style={{ 
                  width: '100%', 
                  maxHeight: 'calc(100vh - 120px)', 
                  height: 'auto', 
                  display: 'block', 
                  objectFit: 'contain' 
                }} 
              />
              {!isViewer && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeImage(url);
                  }}
                  style={{
                    position: 'absolute',
                    top: '0.5rem',
                    right: '0.5rem',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.9)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.5)'}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default JourneySoFar;
